import { useEffect, useMemo, useRef, useState } from "react";
import {
  IScenarioConfig,
  IScenarioFormField,
  IScenarioNode,
  IScenarioNodeTypes,
  ISaveScenarioNode,
  ScenarioAction,
  ScenarioNodeType,
  ScenarioTopic,
} from "../../models/ILiveChatScenario";
import {
  ACTION_META,
  TICKET_PRIORITIES,
  TOKEN_LABELS,
  TYPE_META,
  buildConfigJson,
  descendantIds,
  findNode,
  flattenTree,
  hasTokens,
  opensTicket,
  parseConfig,
  resolvePreview,
  siblingsOf,
  subtreeHeight,
} from "../../utils/scenarioUtils";
import FormFieldsEditor from "./FormFieldsEditor";
import { fitsDepth } from "./ScenarioTree";

export type EditorMode =
  | { kind: "create"; parentId: string | null; nodeType?: ScenarioNodeType }
  | { kind: "edit"; node: IScenarioNode };

interface IdName { id: string; name: string }

interface Props {
  mode: EditorMode;
  tree: IScenarioNode[];
  meta: IScenarioNodeTypes | null;
  tokens: string[];
  departments: IdName[];
  subjects: IdName[];
  saving: boolean;
  /** Hata mesajı döner; başarılıysa null. */
  onSave: (payload: ISaveScenarioNode) => Promise<string | null>;
  onClose: () => void;
  onDelete: (node: IScenarioNode) => void;
  onPreview: (nodeId: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}

const FALLBACK_TYPES: ScenarioNodeType[] = ["Button", "Text", "Otp", "Form", "Ticket"];
const FALLBACK_ACTIONS: ScenarioAction[] = ["None", "HandoffToAgent", "CreateTicket", "CloseConversation"];
const TOKEN_IN_TEXT = /\{\{\s*([\w.]+)\s*\}\}/g;

const num = (v: string) => (v === "" ? undefined : Number(v));

export default function ScenarioNodeEditor({
  mode, tree, meta, tokens, departments, subjects, saving,
  onSave, onClose, onDelete, onPreview, onDirtyChange,
}: Props) {
  const editing = mode.kind === "edit" ? mode.node : null;
  const maxDepth = meta?.maxDepth ?? 6;

  const [parentId, setParentId] = useState<string | null>(editing ? editing.parentId ?? null : mode.kind === "create" ? mode.parentId : null);
  const [text, setText] = useState(editing?.text ?? "");
  const [content, setContent] = useState(editing?.content ?? "");
  const [nodeType, setNodeType] = useState<ScenarioNodeType>(editing?.nodeType ?? (mode.kind === "create" ? mode.nodeType : undefined) ?? "Button");
  const [action, setAction] = useState<ScenarioAction>(editing?.action ?? "None");
  const [topic, setTopic] = useState<string>(editing?.topic && editing.topic !== "Unspecified" ? editing.topic : "");
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [code, setCode] = useState(editing?.code ?? "");
  const [languageCode, setLanguageCode] = useState(editing?.languageCode || "tr");
  const [config, setConfig] = useState<IScenarioConfig>(() => {
    const c = parseConfig(editing?.configJson);
    // Yeni form adımı boş başlamasın
    if (!editing && nodeType === "Form" && !c.form) c.form = { fields: [{ name: "adSoyad", label: "Ad Soyad", type: "text", required: true }] };
    return c;
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rawText, setRawText] = useState("");
  const [rawError, setRawError] = useState("");
  const [orderLinked, setOrderLinked] = useState(true);
  const [error, setError] = useState("");

  const contentRef = useRef<HTMLTextAreaElement>(null);
  const fallbackRef = useRef<HTMLTextAreaElement>(null);
  const lastFocus = useRef<"content" | "fallback">("content");

  const flat = useMemo(() => flattenTree(tree), [tree]);
  const parent = parentId ? findNode(tree, parentId) : null;
  const parentPath = parentId ? flat.find((f) => f.node.id === parentId)?.path ?? [] : [];
  const children = editing?.children ?? [];

  /* ------------------------------- Kayıt ------------------------------- */

  const cleanConfig = (): IScenarioConfig => {
    if (!config.form?.fields) return config;
    return {
      ...config,
      form: {
        ...config.form,
        fields: config.form.fields.map((f) => ({
          ...f,
          name: f.name.trim(),
          label: f.label.trim(),
          options: f.type === "select" ? (f.options ?? []).map((o) => o.trim()).filter(Boolean) : undefined,
          maxLength: f.maxLength || undefined,
        })),
      },
    };
  };

  const sortOrder = useMemo(() => {
    if (editing && (editing.parentId ?? null) === parentId) return editing.sortOrder;
    const sibs = siblingsOf(tree, parentId).filter((n) => n.id !== editing?.id);
    return sibs.length ? Math.max(...sibs.map((n) => n.sortOrder)) + 1 : 0;
  }, [editing, parentId, tree]);

  const payload: ISaveScenarioNode = {
    parentId,
    text: text.trim(),
    content: content.trim() || null,
    nodeType,
    action,
    topic: (topic || null) as ScenarioTopic | null,
    sortOrder,
    isActive,
    languageCode: languageCode.trim() || "tr",
    code: code.trim() || null,
    configJson: buildConfigJson(cleanConfig(), nodeType, action),
  };

  const initialSnapshot = useRef(JSON.stringify(payload));
  const dirty = JSON.stringify(payload) !== initialSnapshot.current;
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  const validate = (): string | null => {
    if (!payload.text) return "Buton yazısı zorunludur.";
    if (nodeType === "Form") {
      const fields = config.form?.fields ?? [];
      if (fields.some((f) => !f.label.trim())) return "Her form alanının bir etiketi olmalı.";
      if (fields.some((f) => !f.name.trim())) return "Her form alanının bir anahtarı olmalı.";
      const names = fields.map((f) => f.name.trim());
      if (new Set(names).size !== names.length) return "Form alanı anahtarları birbirinden farklı olmalı.";
      if (fields.some((f) => f.type === "select" && !(f.options ?? []).some((o) => o.trim()))) return "Seçim listesi alanlarına en az bir seçenek girin.";
    }
    if (rawError) return "Gelişmiş ayarlardaki JSON hatasını düzeltin ya da geri alın.";
    return null;
  };

  const handleSave = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError("");
    const err = await onSave(payload);
    if (err) setError(err);
    else initialSnapshot.current = JSON.stringify(payload);
  };

  /* ---------------------------- Yardımcılar ---------------------------- */

  const patchConfig = (patch: Partial<IScenarioConfig>) => setConfig((c) => ({ ...c, ...patch }));
  const patchSection = <K extends "otp" | "throttle" | "ticket" | "form">(key: K, patch: Partial<NonNullable<IScenarioConfig[K]>>) =>
    setConfig((c) => ({ ...c, [key]: { ...((c[key] as object) ?? {}), ...patch } }));

  const changeType = (t: ScenarioNodeType) => {
    setNodeType(t);
    if (t === "Form" && !(config.form?.fields ?? []).length) {
      patchSection("form", { fields: [{ name: "adSoyad", label: "Ad Soyad", type: "text", required: true }] });
    }
    if (t === "Ticket" && action === "CreateTicket") setAction("None");
  };

  const insertToken = (token: string) => {
    const target = lastFocus.current === "fallback" && fallbackRef.current ? "fallback" : "content";
    const el = target === "fallback" ? fallbackRef.current : contentRef.current;
    const current = target === "fallback" ? config.fallbackContent ?? "" : content;
    const snippet = `{{${token}}}`;
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + snippet + current.slice(end);
    if (target === "fallback") patchConfig({ fallbackContent: next }); else setContent(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + snippet.length, start + snippet.length);
    });
  };

  const openAdvanced = () => {
    setShowAdvanced((v) => !v);
    setRawText(JSON.stringify(parseConfig(buildConfigJson(config, nodeType, action)), null, 2));
    setRawError("");
  };

  const applyRaw = (value: string) => {
    setRawText(value);
    if (!value.trim()) { setConfig({}); setRawError(""); return; }
    try {
      const parsed = JSON.parse(value);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error();
      setConfig(parsed);
      setRawError("");
    } catch {
      setRawError("Geçerli bir JSON nesnesi değil.");
    }
  };

  // Üst adım seçenekleri: kendisi ve altı hariç, derinlik sınırını aşmayanlar
  const parentOptions = useMemo(() => {
    const blocked = editing ? descendantIds(editing) : new Set<string>();
    if (editing) blocked.add(editing.id);
    const height = editing ? subtreeHeight(editing) : 0;
    return flat.map((f) => ({
      ...f,
      disabled: blocked.has(f.node.id) || !fitsDepth(f.depth + 1, height, maxDepth),
    }));
  }, [flat, editing, maxDepth]);

  /* ------------------------------ Uyarılar ------------------------------ */

  const unknownTokens = useMemo(() => {
    const known = new Set(tokens.length ? tokens : Object.keys(TOKEN_LABELS));
    const found = new Set<string>();
    [content, config.fallbackContent ?? ""].forEach((t) => {
      let m: RegExpExecArray | null;
      const re = new RegExp(TOKEN_IN_TEXT.source, "g");
      while ((m = re.exec(t))) if (!known.has(m[1])) found.add(m[1]);
    });
    return Array.from(found);
  }, [content, config.fallbackContent, tokens]);

  const notes: { kind: "info" | "warn"; text: string }[] = [];
  if (parent && (parent.action === "HandoffToAgent" || parent.action === "CloseConversation")) {
    notes.push({ kind: "warn", text: `Üst adım "${parent.text}" sohbeti ${parent.action === "HandoffToAgent" ? "temsilciye bağladığı" : "kapattığı"} için bu adım müşteriye hiç gösterilmez.` });
  }
  let behindGate = false;
  for (let cur = parent; cur; cur = cur.parentId ? findNode(tree, cur.parentId) : null) {
    if (cur.nodeType === "Otp") { behindGate = true; break; }
  }
  if (behindGate) {
    notes.push({ kind: "info", text: "Bu adım bir doğrulama kapısının arkasında — müşteri SMS kodunu girmeden göremez." });
  }
  if ((action === "HandoffToAgent" || action === "CloseConversation") && children.length) {
    notes.push({ kind: "warn", text: `Bu adım sohbeti ${action === "HandoffToAgent" ? "temsilciye bağladığı" : "kapattığı"} için altındaki ${children.length} adım müşteriye sunulmaz.` });
  }
  if (nodeType === "Otp" && editing && !children.length) {
    notes.push({ kind: "info", text: "Doğrulamadan sonra açılacak bir alt adım ekleyin (ör. bir Form). Kapı tek başına bir şey yapmaz." });
  }
  if (nodeType === "Text" && !content.trim()) {
    notes.push({ kind: "warn", text: "Cevap adımının gönderecek metni yok." });
  }
  if (opensTicket(nodeType, action) && !config.ticket?.departmentId) {
    notes.push({ kind: "info", text: "Departman seçilmezse ticket \"GEN\" önekiyle, kimseye atanmadan açılır." });
  }
  if (children.length === 1 && children[0].nodeType !== "Button" && action === "None") {
    notes.push({ kind: "info", text: `Tek alt adım "${children[0].text}" buton olmadığı için müşteri tıklamadan otomatik çalışır.` });
  }
  if (unknownTokens.length) {
    notes.push({ kind: "warn", text: `Tanınmayan değişken: ${unknownTokens.map((t) => `{{${t}}}`).join(", ")} — müşteriye boş görünür.` });
  }

  const preview = resolvePreview(content, config, orderLinked);
  const showOrderSection = hasTokens(content) || !!config.requiresOrder || !!config.fallbackContent;
  const typeChoices = meta?.nodeTypes?.length ? meta.nodeTypes : FALLBACK_TYPES.map((t, i) => ({ value: i, name: t, label: TYPE_META[t].label, description: "" }));
  const actionChoices = meta?.actions?.length ? meta.actions : FALLBACK_ACTIONS.map((a, i) => ({ value: i, name: a, label: ACTION_META[a]?.label ?? "Yok", description: "" }));
  const selectedAction = actionChoices.find((a) => a.name === action);
  const topicChoices = (meta?.topics ?? []).filter((t) => t.name !== "Unspecified");
  const selectedTopic = topicChoices.find((t) => t.name === topic);
  const tokenList = tokens.length ? tokens : Object.keys(TOKEN_LABELS);
  const typeMeta = TYPE_META[nodeType];

  return (
    <div className="lcs-card lcs-editor">
      {/* Başlık */}
      <div className="lcs-editor-head">
        <div style={{ minWidth: 0, display: "flex", gap: 10, alignItems: "center" }}>
          <span className="lcs-type-icon" style={{ width: 36, height: 36, borderRadius: 10, background: typeMeta.bg, color: typeMeta.color, fontSize: "1rem" }}>
            <i className={`bi ${typeMeta.icon}`}></i>
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827" }}>{editing ? "Adımı düzenle" : "Yeni adım"}</div>
            <div style={{ fontSize: ".74rem", color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <i className="bi bi-signpost-split me-1"></i>
              {parentPath.length ? `Kök › ${parentPath.join(" › ")}` : "Açılış butonu (kök)"}
            </div>
          </div>
        </div>
        <button type="button" className="lcs-icon-btn lcs-editor-close" onClick={onClose} aria-label="Kapat"><i className="bi bi-x-lg"></i></button>
      </div>

      <div className="lcs-editor-body">
        {/* Müşterinin gördüğü */}
        <section>
          <div className="lcs-section-title"><i className="bi bi-person-fill"></i>Müşterinin gördüğü</div>

          <div className="lcs-label">
            <span>Buton yazısı <span style={{ color: "#dc2626" }}>*</span></span>
            <span style={{ fontWeight: 500, color: text.length > 110 ? "#d97706" : "#9ca3af" }}>{text.length}/120</span>
          </div>
          <input className="lcs-input" value={text} maxLength={120} onChange={(e) => setText(e.target.value)}
            placeholder="Ör. Siparişim nerede?" autoFocus={!editing} />
          <div className="lcs-hint">Müşteri bu yazıya tıklar; seçince sohbete müşterinin mesajı olarak da düşer.</div>

          <div className="lcs-label" style={{ marginTop: 14 }}>
            <span>{nodeType === "Form" ? "Formun üstündeki açıklama" : nodeType === "Otp" ? "Kod gönderilince yazılacak mesaj" : "Asistanın cevabı"}</span>
            <span style={{ fontWeight: 500, color: content.length > 1900 ? "#d97706" : "#9ca3af" }}>{content.length}/2000</span>
          </div>
          <textarea ref={contentRef} className="lcs-input" rows={4} maxLength={2000} value={content}
            onFocus={() => { lastFocus.current = "content"; }}
            onChange={(e) => setContent(e.target.value)}
            placeholder={nodeType === "Button" ? "Ör. Siparişinizle ilgili hangi konuda yardımcı olabilirim? (boş bırakılırsa varsayılan soru gösterilir)" : "Ör. Merhaba {{customer.name}}, siparişiniz yaklaşık {{order.remainingMinutes}} dakika içinde kapınızda."} />

          <div style={{ marginTop: 8 }}>
            <div className="lcs-hint" style={{ marginBottom: 6, marginTop: 0 }}>
              <i className="bi bi-magic me-1"></i>Değişken ekle — sohbet anındaki gerçek bilgiyle dolar:
            </div>
            <div className="lcs-chips">
              {tokenList.map((t) => (
                <button key={t} type="button" className="lcs-chip lcs-chip--token" title={`{{${t}}}`} onClick={() => insertToken(t)}>
                  {TOKEN_LABELS[t] ?? t}
                </button>
              ))}
            </div>
          </div>

          {preview && (
            <div style={{ marginTop: 12, background: "#f3f4f6", borderRadius: 14, padding: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: ".7rem", fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".05em" }}>
                  <i className="bi bi-eye me-1"></i>Önizleme (örnek verilerle)
                </span>
                {hasTokens(content) && (
                  <div className="lcs-chips">
                    <button type="button" className={`lcs-chip${orderLinked ? " lcs-chip--active" : ""}`} onClick={() => setOrderLinked(true)}>Sipariş var</button>
                    <button type="button" className={`lcs-chip${!orderLinked ? " lcs-chip--active" : ""}`} onClick={() => setOrderLinked(false)}>Sipariş yok</button>
                  </div>
                )}
              </div>
              <div className="lcs-bubble lcs-bubble--bot">{preview}</div>
            </div>
          )}
        </section>

        {/* Adım türü */}
        <section>
          <div className="lcs-section-title"><i className="bi bi-grid-fill"></i>Adım türü</div>
          <div className="lcs-type-grid">
            {typeChoices.map((t) => {
              const m = TYPE_META[t.name] ?? TYPE_META.Button;
              const active = nodeType === t.name;
              return (
                <button key={t.name} type="button" className={`lcs-type-card${active ? " lcs-type-card--active" : ""}`} onClick={() => changeType(t.name)}>
                  <span style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 800, fontSize: ".82rem", color: "#111827" }}>
                    <span className="lcs-type-icon" style={{ width: 26, height: 26, borderRadius: 8, fontSize: ".75rem", background: m.bg, color: m.color }}>
                      <i className={`bi ${m.icon}`}></i>
                    </span>
                    {t.label}
                  </span>
                  {t.description && <span style={{ fontSize: ".7rem", color: "#6b7280", lineHeight: 1.35 }}>{t.description}</span>}
                </button>
              );
            })}
          </div>
        </section>

        {/* Sonuç */}
        <section>
          <div className="lcs-section-title"><i className="bi bi-lightning-charge-fill"></i>Adım çalışınca</div>
          <div className="lcs-chips">
            {actionChoices.map((a) => {
              const m = ACTION_META[a.name];
              return (
                <button key={a.name} type="button" className={`lcs-chip${action === a.name ? " lcs-chip--active" : ""}`} onClick={() => setAction(a.name)}>
                  {m && <i className={`bi ${m.icon}`}></i>}{a.label}
                </button>
              );
            })}
          </div>
          {selectedAction?.description && <div className="lcs-hint">{selectedAction.description}</div>}

          {topicChoices.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="lcs-label">Sohbetin konusu</div>
              <select className="lcs-input" value={topic} onChange={(e) => setTopic(e.target.value)}>
                <option value="">Konuyu değiştirme</option>
                {topicChoices.map((t) => <option key={t.name} value={t.name}>{t.label}</option>)}
              </select>
              <div className="lcs-hint">{selectedTopic?.description ?? "Seçilirse temsilci kuyruğu ve paneli sohbeti bu konuyla görür."}</div>
            </div>
          )}
        </section>

        {/* Sipariş yoksa */}
        {showOrderSection && (
          <section>
            <div className="lcs-section-title"><i className="bi bi-bag-x-fill"></i>Sipariş bilgisi yoksa</div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: ".8rem", fontWeight: 600, color: "#374151", cursor: "pointer", marginBottom: 10 }}>
              <input type="checkbox" checked={!!config.requiresOrder} onChange={(e) => patchConfig({ requiresOrder: e.target.checked })} />
              Sohbete bağlı sipariş yoksa cevabı hiç gönderme, aşağıdaki alternatif metni gönder
            </label>
            <div className="lcs-label">Alternatif metin</div>
            <textarea ref={fallbackRef} className="lcs-input" rows={2} maxLength={2000} value={config.fallbackContent ?? ""}
              onFocus={() => { lastFocus.current = "fallback"; }}
              onChange={(e) => patchConfig({ fallbackContent: e.target.value })}
              placeholder="Ör. Sohbete bağlı bir siparişiniz görünmüyor. Sipariş numaranızı yazarsanız yardımcı olalım." />
            <div className="lcs-hint">Boş bırakılırsa dolmayan değişkenler metinden silinir; müşteri asla {"{{...}}"} görmez.</div>
          </section>
        )}

        {/* Doğrulama */}
        {nodeType === "Otp" && (
          <section>
            <div className="lcs-section-title"><i className="bi bi-shield-lock-fill"></i>SMS doğrulama ayarları</div>
            <div className="lcs-grid-4">
              {([
                ["expiryMinutes", "Kod süresi (dk)", 5, 1, 30],
                ["maxAttempts", "Deneme hakkı", 5, 1, 10],
                ["cooldownSeconds", "Yeni kod beklemesi (sn)", 120, 0, 3600],
                ["maxPerDay", "Günlük kod sınırı", 5, 1, 50],
              ] as [keyof NonNullable<IScenarioConfig["otp"]>, string, number, number, number][]).map(([key, label, def, min, max]) => (
                <div key={key}>
                  <div className="lcs-label" style={{ minHeight: 30, alignItems: "flex-end" }}>{label}</div>
                  <input type="number" className="lcs-input" min={min} max={max} placeholder={String(def)}
                    value={config.otp?.[key] ?? ""} onChange={(e) => patchSection("otp", { [key]: num(e.target.value) })} />
                  <div className="lcs-hint">{min}–{max}</div>
                </div>
              ))}
            </div>
            <div className="lcs-hint">Boş bırakılan ayar varsayılanı kullanır. Kod hesabın kayıtlı telefonuna gider; günlük sınır kişi başına sayılır.</div>
          </section>
        )}

        {/* Form */}
        {nodeType === "Form" && (
          <section>
            <div className="lcs-section-title"><i className="bi bi-ui-checks"></i>Form alanları</div>
            <FormFieldsEditor fields={config.form?.fields ?? []} onChange={(fields: IScenarioFormField[]) => patchSection("form", { fields })} />
            <div style={{ marginTop: 12 }}>
              <div className="lcs-label">Gönder butonu yazısı</div>
              <input className="lcs-input" value={config.form?.submitLabel ?? ""} placeholder="Gönder"
                onChange={(e) => patchSection("form", { submitLabel: e.target.value })} />
            </div>
          </section>
        )}

        {/* Ticket */}
        {opensTicket(nodeType, action) && (
          <section>
            <div className="lcs-section-title"><i className="bi bi-ticket-perforated-fill"></i>Açılacak ticket</div>
            <div className="lcs-label">Ticket başlığı</div>
            <input className="lcs-input" value={config.ticket?.title ?? ""} maxLength={200} placeholder={text || "Boş bırakılırsa buton yazısı kullanılır"}
              onChange={(e) => patchSection("ticket", { title: e.target.value })} />
            <div className="lcs-grid-2" style={{ marginTop: 10 }}>
              <div>
                <div className="lcs-label">Departman</div>
                <select className="lcs-input" value={config.ticket?.departmentId ?? ""} onChange={(e) => patchSection("ticket", { departmentId: e.target.value || undefined })}>
                  <option value="">Seçilmedi</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <div className="lcs-label">Konu</div>
                <select className="lcs-input" value={config.ticket?.subjectId ?? ""} onChange={(e) => patchSection("ticket", { subjectId: e.target.value || undefined })}>
                  <option value="">Seçilmedi</option>
                  {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <div className="lcs-label">Öncelik</div>
                <select className="lcs-input" value={config.ticket?.priority ?? ""} onChange={(e) => patchSection("ticket", { priority: num(e.target.value) })}>
                  <option value="">Varsayılan</option>
                  {TICKET_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <div className="lcs-label">Günlük gönderim sınırı</div>
                <input type="number" min={0} max={100} className="lcs-input" placeholder="5" value={config.throttle?.maxPerDay ?? ""}
                  onChange={(e) => patchSection("throttle", { maxPerDay: num(e.target.value) })} />
              </div>
            </div>
            <div className="lcs-hint">Aynı sohbette bu adım bir kez tamamlanabilir; günlük sınır kişi başına sayılır.</div>
          </section>
        )}

        {/* Notlar */}
        {notes.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {notes.map((n, i) => (
              <div key={i} className={`lcs-note lcs-note--${n.kind}`}>
                <i className={`bi ${n.kind === "warn" ? "bi-exclamation-triangle-fill" : "bi-info-circle-fill"}`}></i>
                <span>{n.text}</span>
              </div>
            ))}
          </div>
        )}

        {/* Gelişmiş */}
        <section>
          <button type="button" className="lcs-btn lcs-btn--ghost" style={{ padding: "4px 0", color: "#6b7280" }} onClick={openAdvanced}>
            <i className={`bi ${showAdvanced ? "bi-chevron-down" : "bi-chevron-right"}`}></i>Gelişmiş ayarlar
          </button>
          {showAdvanced && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 10 }}>
              <div>
                <div className="lcs-label">Bağlı olduğu adım</div>
                <select className="lcs-input" value={parentId ?? ""} onChange={(e) => setParentId(e.target.value || null)}>
                  <option value="">— Açılış butonu (kök) —</option>
                  {parentOptions.map((f) => (
                    <option key={f.node.id} value={f.node.id} disabled={f.disabled}>
                      {"  ".repeat(f.depth)}{f.depth ? "└ " : ""}{f.node.text}
                    </option>
                  ))}
                </select>
                <div className="lcs-hint">Ağaçta sürükleyerek de taşıyabilirsiniz.</div>
              </div>
              <div className="lcs-grid-2">
                <div>
                  <div className="lcs-label">Kod (opsiyonel)</div>
                  <input className="lcs-input lcs-input--mono" value={code} maxLength={60} placeholder="ör. delivery-eta" onChange={(e) => setCode(e.target.value)} />
                  <div className="lcs-hint">Kodun referans verebileceği benzersiz anahtar.</div>
                </div>
                <div>
                  <div className="lcs-label">Dil</div>
                  <input className="lcs-input" value={languageCode} maxLength={10} onChange={(e) => setLanguageCode(e.target.value)} />
                </div>
              </div>
              <div>
                <div className="lcs-label">Ayarlar (JSON)</div>
                <textarea className="lcs-input lcs-input--mono" rows={8} value={rawText} onChange={(e) => applyRaw(e.target.value)} spellCheck={false} />
                {rawError
                  ? <div className="lcs-hint" style={{ color: "#dc2626" }}>{rawError}</div>
                  : <div className="lcs-hint">Yukarıdaki alanlar bu JSON'u oluşturur. Burada değiştirdiğiniz geçerli JSON hemen uygulanır.</div>}
              </div>
            </div>
          )}
        </section>

        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: ".84rem", fontWeight: 700, color: "#374151", cursor: "pointer" }}>
          <button type="button" className={`lcs-switch ${isActive ? "lcs-switch--on" : "lcs-switch--off"}`} onClick={() => setIsActive((v) => !v)} aria-label="Aktiflik" />
          {isActive ? "Aktif — müşteriye gösterilir" : "Pasif — müşteriye gösterilmez"}
        </label>

        {error && (
          <div className="lcs-note lcs-note--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
        )}
      </div>

      {/* Alt çubuk */}
      <div className="lcs-editor-foot">
        <div style={{ display: "flex", gap: 6 }}>
          {editing && (
            <>
              <button type="button" className="lcs-btn lcs-btn--danger" onClick={() => onDelete(editing)}><i className="bi bi-trash3"></i><span className="d-none d-sm-inline">Sil</span></button>
              <button type="button" className="lcs-btn" onClick={() => onPreview(editing.id)} disabled={dirty} title={dirty ? "Önce değişiklikleri kaydedin" : "Bu adımdan başlayarak önizle"}>
                <i className="bi bi-play-circle"></i><span className="d-none d-sm-inline">Önizlemede dene</span>
              </button>
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" className="lcs-btn" onClick={onClose}>Vazgeç</button>
          <button type="button" className="lcs-btn lcs-btn--primary" disabled={saving || !text.trim() || (!!editing && !dirty)} onClick={handleSave}>
            {saving ? <><span className="spinner-border spinner-border-sm"></span>Kaydediliyor</> : <><i className="bi bi-check-lg"></i>{editing ? "Kaydet" : "Oluştur"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
